// T054: Create FeedList component with T056: infinite scroll container

import { useCallback } from 'react'
import { PostCard } from './PostCard'
import { PostSkeleton } from './PostSkeleton'
import { PullToRefresh } from './PullToRefresh'
import { Button } from '@/components/ui/button'
import { useFeed, getAllPosts } from '@/hooks/useFeed'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useAuthStore } from '@/stores/authStore'
import { RefreshCw } from 'lucide-react'

export function FeedList() {
  const user = useAuthStore((state) => state.user)

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFeed({
    username: user?.username || '',
  })

  const loadMoreRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  })

  const posts = getAllPosts(data)

  const handleRefresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <PostSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-6 text-center">
        <p className="text-destructive mb-4">
          {error instanceof Error ? error.message : 'Failed to load feed'}
        </p>
        <Button
          variant="outline"
          onClick={() => refetch()}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Try Again
        </Button>
      </div>
    )
  }

  if (posts.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-12 text-center">
        <p className="text-lg text-muted-foreground">
          No posts found. Follow some users on Steemit to see their posts here!
        </p>
      </div>
    )
  }

  return (
    <PullToRefresh onRefresh={handleRefresh} className="h-full">
      <div className="space-y-4">
        {posts.map((post) => (
          <PostCard key={`${post.author}-${post.permlink}`} post={post} />
        ))}

        {/* Infinite scroll trigger */}
        <div ref={loadMoreRef} className="py-4">
          {isFetchingNextPage && <PostSkeleton />}
          {hasNextPage && !isFetchingNextPage && (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                onClick={() => fetchNextPage()}
                className="gap-2"
              >
                Load More
              </Button>
            </div>
          )}
          {!hasNextPage && posts.length > 0 && (
            <p className="text-center text-sm text-muted-foreground">
              You've reached the end of the feed
            </p>
          )}
        </div>
      </div>
    </PullToRefresh>
  )
}
